import { useCallback, useEffect, useId, useRef, useState, useSyncExternalStore, type JSX, type ReactNode } from 'react';

import { ConfirmSurface } from './ConfirmSurface';
import {
  closeFunctionalDialog,
  disposeDialogs,
  getDialogs,
  listenerCount,
  registerDialogHost,
  removeDialogEntry,
  settleConfirmDialog,
  subscribeDialogs,
  unregisterDialogHost,
  type ConfirmDialogEntry,
  type FunctionalDialogEntry,
} from './dialogService';
import { OVERLAY_MOTION_MS, prefersReducedMotion, scheduleAfterPaint, type OverlayMotion } from './motion';
import { Overlay } from './Overlay';

/**
 * 弹窗宿主。
 *
 * 它是**唯一**渲染弹窗的地方：调用点拿到的 `confirm()` 只是向服务里投一份请求，
 * 真正长出来的界面在这里。这样两件事才有保证：
 *   - 所有弹窗长得一样（样式与动效只有一份实现）；
 *   - 弹窗不随调用点所在的子树卸载而消失（例如从地图气泡里发起的确认）。
 *
 * 把它挂在应用根部即可（GCS 挂在 `#root` 里，与 `#root` 自身的定位约束见 `Overlay.tsx`）。
 * **只挂一处**：多处宿主会让同一次请求被渲染成两份（见 `dialogService.ts` 的单宿主约定）。
 *
 * 两类弹窗的差别只在这一个文件里的两行 `scope` —— 这正是"用哪一个在调用点一眼可见"的落点：
 * `useConfirm()` → `ConfirmDialogHost`（realtime：锚在按钮上、无蒙版、点外部=取消）；
 * `useDialog()`  → `FunctionalDialogHost`（blocking：居中、模糊蒙版、即时出现）。
 */

export interface ConfirmDialogProviderProps {
  children: ReactNode;
}

export function ConfirmDialogProvider({ children }: ConfirmDialogProviderProps): JSX.Element {
  const entries = useSyncExternalStore(subscribeDialogs, getDialogs, getDialogs);

  /*
   * 渲染期登记（而不是 effect 里）：子组件的**第一次渲染**就会调用 useConfirm()，
   * 那时宿主的 effect 还没跑；不在这里登记的话，"有没有宿主"在首次渲染时永远是否定的，
   * 于是正常用法会被报成配置错误。登记只是置一个幂等标记（见 dialogService.ts）。
   */
  registerDialogHost();

  useEffect(
    () => () => {
      unregisterDialogHost();
      // 宿主消失 = 再也没有人能回答用户的确认。此时让等待中的 Promise 以 `false` 落定，
      // 否则 `await confirm(...)` 会永远挂起：表现为"点了按钮什么都没发生"，比报错难查得多。
      if (listenerCount() === 0) disposeDialogs();
    },
    [],
  );

  return (
    <>
      {children}
      {entries.map((entry) =>
        entry.kind === 'confirm' ? (
          <ConfirmDialogHost key={entry.id} entry={entry} />
        ) : (
          <FunctionalDialogHost key={entry.id} entry={entry} />
        ),
      )}
    </>
  );
}

/**
 * 按钮确认气泡。
 *
 * 作用域是 **`realtime`**（不是 `blocking`）：它锚在按钮上、不铺蒙版。
 * 理由与 scope.ts 的默认值同源 —— 蒙版会遮住姿态与电压，而飞行员判断"该不该按下去"
 * 靠的正是它们。`realtime` 同时带来"点击外部 = 取消"这个符合直觉的逃逸通道。
 *
 * 档位是 **`confirm`**（最高）：作用域管"遮不遮遥测"，档位管"压在谁上面"，两者独立。
 * 没有这一档的话，「打开参数详情 → 写入 → 确认」这条真实流程里的确认气泡
 * 会落在功能性弹窗的蒙版之下 —— 用户只看见一层压暗的、点不动的问句。
 */
function ConfirmDialogHost({ entry }: { entry: ConfirmDialogEntry }): JSX.Element {
  const options = entry.options;
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  // 后果那段要被"面板 + 取消按钮"引用（读屏在初始焦点上就要念出会发生什么）
  const consequenceId = useId();
  // 起始态只存活一帧：下一帧切到 settled，transition 才有起点（见 motion.scheduleAfterPaint）
  const [motion, setMotion] = useState<OverlayMotion>(() => (prefersReducedMotion() ? 'settled' : 'entering'));
  // 以 store 里的 closing 为准（它可能由 closeAllDialogs 这类外部关闭设置）：两边都算收回
  const closing = motion === 'exiting' || entry.closing;

  useEffect(() => {
    if (motion !== 'entering') return undefined;
    return scheduleAfterPaint(() => setMotion('settled'));
  }, [motion]);

  useEffect(() => {
    if (!closing) return undefined;
    // 条目的移除交给动画收尾：动画还没播完就从 DOM 里消失，用户看到的就不是"收回按钮里"。
    // 期间面板是 pointer-events: none（见 styles.ts），因此这 140ms 不会吞掉任何点击。
    const timer = setTimeout(() => removeDialogEntry(entry.id), OVERLAY_MOTION_MS);
    return () => clearTimeout(timer);
  }, [closing, entry.id]);

  const settle = useCallback(
    (confirmed: boolean): void => {
      // 立刻落定，不等动画：让调用方为 140ms 的视觉效果多等一等没有任何好处。
      // 重复调用（先 Esc 再点取消之类）由服务侧幂等吸收。
      settleConfirmDialog(entry.id, confirmed);
      setMotion('exiting');
    },
    [entry.id],
  );

  return (
    <Overlay
      open
      scope="realtime"
      tier="confirm"
      role="alertdialog"
      label={options.title}
      title={options.title}
      describedBy={consequenceId}
      anchor={options.anchor}
      initialFocus={cancelRef}
      onClose={() => settle(false)}
      maxWidth={440}
      // 显式声明无阴影：确认气泡紧贴触发按钮，"浮起来"的阴影会让它看起来像另一个东西
      panelShadow="none"
      motion={closing ? 'exiting' : motion}
    >
      <ConfirmSurface
        danger={options.danger}
        consequence={options.consequence}
        consequenceId={consequenceId}
        confirmLabel={options.confirmLabel}
        confirmPhrase={options.confirmPhrase}
        cancelLabel={options.cancelLabel}
        detail={options.detail}
        cancelRef={cancelRef}
        onConfirm={() => settle(true)}
        onCancel={() => settle(false)}
      />
    </Overlay>
  );
}

/**
 * 功能性弹窗。
 *
 * 作用域是 **`blocking`**：居中 + 模糊蒙版 + 锁滚动 + 焦点约束 + Esc 关闭。
 * 它**没有动画**（`motion` 不传）：这是一块"临时页面"，出现/消失就该是即时的；
 * 表演性的飞入还会让人以为它是从某处飘来的、可以飘走。
 */
function FunctionalDialogHost({ entry }: { entry: FunctionalDialogEntry }): JSX.Element {
  const options = entry.options;
  return (
    <Overlay
      open
      scope="blocking"
      label={options.title}
      title={options.title}
      verticalAlign="center"
      maxWidth={options.maxWidth ?? 520}
      onClose={() => closeFunctionalDialog(entry.id)}
    >
      {options.content}
    </Overlay>
  );
}
